import XMLParser from 'react-xml-parser';

import AsyncStorage from '@react-native-async-storage/async-storage';

import { GeocomplyPayload } from '@/data/location/types';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { read, save } from '@/utils/async-storage';
import { isWeb } from '@/utils/constants-platform-specific';
import { logger } from '@/utils/logging';

const addWeb = (key: string) => (isWeb ? `${key}Web` : key);

const GEO_PAYLOAD_KEY = addWeb('storageGeoPayloadKey');
const GEO_ENCODED_PAYLOAD_KEY = addWeb('storageGeoEncodedPayloadKey');
const GEO_LICENSE_KEY = addWeb('storageGeoLicenseKey');

export const LOG_TAG = isWeb ? '[GeoComplyWeb]' : '[GeoComply]';

/**
 * Maps product to geolocation token cache key.
 * Casino and Sweeps share the same geocomply license,
 * so they use the same cache key (sweeps-casino).
 */
export const mapProductToGeotokenKey = (product: Product): string => {
    // Treat Casino and Sweeps as the same product for geolocation purposes [CSP-1862]
    if (product === Product.Casino || product === Product.Sweeps) {
        const key = `${Product.Sweeps}-${Product.Casino}`;
        return key;
    }
    return product;
};

export abstract class GeocomplyClientBase {
    product: Product;
    jurisdiction: string;

    isInitialized = false;

    license: string | undefined;
    userId: string = 'none';

    STORAGE_GEO_LICENSE_KEY: string;
    STORAGE_GEO_PAYLOAD_KEY: string;
    STORAGE_GEO_ENCODED_PAYLOAD_KEY: string;

    DECODE_PAYLOAD_URL: string;
    LICENSE_SERVER_URL: string;

    constructor({ product, jurisdiction }: { product: Product; jurisdiction: string }) {
        this.product = product;
        this.jurisdiction = jurisdiction;

        const productKey = mapProductToGeotokenKey(product);
        this.STORAGE_GEO_LICENSE_KEY = `${GEO_LICENSE_KEY}-${productKey}-${jurisdiction}`;
        this.STORAGE_GEO_PAYLOAD_KEY = `${GEO_PAYLOAD_KEY}-${productKey}-${jurisdiction}`;
        this.STORAGE_GEO_ENCODED_PAYLOAD_KEY = `${GEO_ENCODED_PAYLOAD_KEY}-${productKey}-${jurisdiction}`;

        const URLS = useJurisdictionStore.getState().jurisdictionSettings?.productConfig?.keyValuePairs?.URLS ?? {};

        this.DECODE_PAYLOAD_URL = URLS.GEOCOMPLY_DECODE_PAYLOAD_URL;
        this.LICENSE_SERVER_URL = URLS.GEOCOMPLY_LICENSE_SERVER_URL;

        console.log(LOG_TAG, 'NEW CLIENT', {
            product,
            jurisdiction,
            URLS,
            LICENSE_SERVER_URL: this.LICENSE_SERVER_URL,
        });
    }

    async init({ userId }: { userId: string }): Promise<void> {
        this.userId = userId;
        logger.debug(LOG_TAG, `Init Geocomply Client @${this.product}. Check license...`);
        const saved = await read(this.STORAGE_GEO_LICENSE_KEY);
        if (Date.parse(saved?.expires) >= Date.now()) {
            logger.debug(LOG_TAG, 'Cached license is not expired');
            this.license = saved.license;
        }

        const res = await this.requestLicense();
        this.license = res.license;

        logger.debug(LOG_TAG, 'Request license response', {
            license: res.license?.substring(0, 10) + '...',
            expires: res.expires,
        });
        await save(this.STORAGE_GEO_LICENSE_KEY, res);

        this.listenToIpDetection();
        this.isInitialized = true;
    }

    async requestLicense() {
        try {
            logger.debug(LOG_TAG, 'URLS.LICENSE_SERVER_URL', this.LICENSE_SERVER_URL);
            const url = isWeb
                ? `${this.LICENSE_SERVER_URL}${this.LICENSE_SERVER_URL.includes('?') ? '&' : '?'}clientType=WEB`
                : this.LICENSE_SERVER_URL;
            const response = await fetch(url, {
                headers: {
                    Authorization: useUserStore.getState().session?.access_token ?? '',
                },
            });

            const textResponse = await response.text();
            const xml = new XMLParser().parseFromString(textResponse);

            const licenseTag = xml.getElementsByTagName('license')[0];

            if (!licenseTag) {
                logger.error(LOG_TAG, 'LICENSE SERVER ERROR', textResponse);
                return { textResponse };
            }

            return {
                license: licenseTag?.value,
                expires: licenseTag?.attributes?.expires,
            };
        } catch (error) {
            logger.warn(LOG_TAG, 'geocomply-request license error', error);
            return {};
        }
    }

    async getDecodedPayloadRequest(data: string) {
        return await fetch(this.DECODE_PAYLOAD_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ payload: data }),
        });
    }

    async decodeAndSavePayload(data: string): Promise<GeocomplyPayload> {
        let res = await this.getDecodedPayloadRequest(data);
        if (res.status !== 200) {
            logger.error(LOG_TAG, 'Decode Payload Error', { response: res }, res.status);
        }
        const decoded = JSON.parse(await res.text());
        logger.info(LOG_TAG, 'Decoded payload', decoded);

        await save(this.STORAGE_GEO_PAYLOAD_KEY, decoded);
        await save(this.STORAGE_GEO_ENCODED_PAYLOAD_KEY, data);

        return decoded;
    }

    async decodePayloadOnly(data: string): Promise<GeocomplyPayload> {
        let res = await this.getDecodedPayloadRequest(data);
        if (res.status !== 200) {
            logger.error(LOG_TAG, 'Decode Payload Error', { response: res }, res.status);
        }
        const decoded = JSON.parse(await res.text());
        logger.info(LOG_TAG, 'Decoded payload (not cached)', decoded);

        return decoded;
    }

    async shutdownAndLogout() {
        this.shutdown();
        await this.removeAllGeoPayloadsFromStorage();
    }

    protected async removeAllGeoPayloadsFromStorage() {
        //Remove all stored geocomply keys
        //This removes keys for all products and all jurisdictions on logout
        const allKeys = await AsyncStorage.getAllKeys();
        const keysToRemove = allKeys.filter(
            key => key.startsWith(GEO_PAYLOAD_KEY) || key.startsWith(GEO_ENCODED_PAYLOAD_KEY)
        );
        await AsyncStorage.multiRemove(keysToRemove);
    }

    abstract request(options: {
        readCache: boolean;
        reason?: string;
        reasonCode?: string;
        skipSave?: boolean;
    }): Promise<{ encoded: string; decoded: GeocomplyPayload }>;

    abstract listenToIpDetection(): void;

    abstract shutdown(): void;
}
